import { useState, FC, useRef, Fragment, useEffect } from 'react';
import { Card } from '@mui/material'
import { gql, useMutation, useQuery } from '@apollo/client';
import * as types from "../gql/graphql";
import Checkbox from '@mui/material/Checkbox';
import { useTheme } from '@mui/material/styles';

import {
    Popover,
    List,
    ListItem,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from '@mui/material';

import BoxRoot from './MDBox/MDBoxRoot';
import ButtonRoot from './MDButton/MDButtonRoot';
import TypographyRoot from './MDTypography/MDTypographyRoot';
import { useScreenSize } from "../hooks/ScreenSizeContext";
import { useFetchAll, useQueryAll } from '../apolloClient';
import { SchoolTime } from '../utils/SchoolTime';

const getEventInstructorsQuery = gql(`
query GetEventInstructors($event: BigInt!, $after: Cursor) {
  fs_instructoreventsCollection(filter: {event: {eq: $event}}, after: $after) {
    pageInfo { endCursor startCursor hasNextPage }
    edges {
      node {
        id
        on_deck
        fs_admins {
          first_name
          last_name
        }
      }
    }
  }
}`);

const getEventHistoryQuery = gql(`
query GetEventHistory($event: BigInt!, $after: Cursor, $first: Int) {
  fs_attendancehistoryCollection(
    filter: {event: {eq: $event}}
    first: $first
    after: $after
  ) {
    pageInfo {
      endCursor
      startCursor
      hasNextPage
    }
    edges {
      node {
            __typename
            nodeId
            id
            class
            classname
            datetaken
            student
            event
      }
    }
  }
}`);

const deleteAttendanceQuery = gql(`
    mutation deleteAttendanceQuery($filter:fs_attendancehistoryFilter) {
        deleteFromfs_attendancehistoryCollection(filter: $filter) {
            records {
                __typename
                nodeId
                id
                class
                classname
                datetaken
                student
                event
            }
            affectedCount
        }
    }
    `);

const addAttendanceQuery = gql(`
    mutation addAttendanceDataQuery($ah: fs_attendancehistoryInsertInput!) {
        insertIntofs_attendancehistoryCollection(objects: [$ah]) {
            affectedCount
            records {
                __typename
                nodeId
                id
                class
                classname
                datetaken
                student
                event
            }            
        }
    }       
    `);

interface EditEventInstructorsProps {
    event: types.Fs_Events;
    attendies: types.Fs_SignupEdge[];
}

type StudentListType = {
    id: number,
    FirstName?: string,
    LastName?: string,
    Height?: string,
    Weight?: number,
    MedCondFlag?: boolean,
    Package?: string,
    attended: boolean,
    histId: number,
    Mobile?: string,
    GearAndYouth?: string
}

const EventRoster: FC<EditEventInstructorsProps> = ({ event, attendies }) => {
    const theme = useTheme();
    const isSmallScreen = useScreenSize();
    const [anchorElMap, setAnchorElMap] = useState<{ [key: number]: HTMLElement | null }>({});


    const signups = attendies;
    const [followupDialogOpen, setFollowupDialogOpen] = useState(false);
    const [followupPayload, setFollowupPayload] = useState<null | { base: { subject: string; body: string }; recipients: Array<{ email: string; firstName: string }> }>(null);
    const [sendingFollowup, setSendingFollowup] = useState(false);
    const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });
    const { data: instructors } = useQueryAll<types.Fs_InstructoreventsEdge>({ query: getEventInstructorsQuery, variables: { event: event.id } });

    const { items: attendance, loading, error, progress, fetchAll } = useFetchAll<{ fs_attendancehistoryCollection: types.Fs_AttendancehistoryConnection}, any, types.Fs_AttendancehistoryEdge>();
    /*const { data:attendanceD, loading, error} = useQuery<{ fs_attendancehistoryCollection: types.Fs_AttendancehistoryConnection}>(getEventHistoryQuery, {
        variables: { event: event.id, first: 100 },
        //fetchPolicy: "network-only",
    });
    const attendance = attendanceD?.fs_attendancehistoryCollection.edges || [];*/


    const [refetchAttendance, setRefetchAttendance] = useState(false);

    
    useEffect(() => {
        fetchAll(
            {
                query: getEventHistoryQuery,
                variables: { event: event.id },
                getPageInfo: (data) => data.fs_attendancehistoryCollection.pageInfo,
                getItems: (data) => data.fs_attendancehistoryCollection.edges
            });
    }, [refetchAttendance]);

    const [addAttendanceMutation, { data: add_data, loading: add_loading, error: add_error, reset: addReset }] = useMutation(addAttendanceQuery, {
        refetchQueries: [
    //    'GetEventHistory'
        ],
    });

    const [deleteAttendanceMutation, { loading: deleteAttendanceLoading, error: deleteAttendanceError }] = useMutation(deleteAttendanceQuery, {
        refetchQueries: [
    //    'GetEventHistory'
        ],
    });


    if (!signups || !attendance || event.id == -1) return null;

    const registered = countSignupsWithStatusZero(signups);

    const handlePopoverOpen = (studentId: number) => (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElMap(prev => ({ ...prev, [studentId]: event.currentTarget }));
    };

    const handlePopoverClose = (studentId: number) => () => {
        setAnchorElMap(prev => ({ ...prev, [studentId]: null }));
    };

    const rstudents = [];
    signups.forEach(signup => {
        if (signup.node.status !== 2) { // Ignore waitlisted
            const student = signup.node.fs_registrations.fs_purchases.fs_students;
            // Format mobile number as xxx-xxx-xxxx if needed
            let formattedMobile = student.Mobile;
            if (student.Mobile) {
                const cleaned = student.Mobile.replace(/\D/g, '');
                if (cleaned.length === 10) {
                    formattedMobile = `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
                }
            }
            const histRecord = attendance.find((element) => element.node.student === student.id);
            // Determine if student is a youth (under 18) using plain Date arithmetic
            let youthTag = "";
            if (student.DOB) {
                const dobDate = new Date(student.DOB);
                const now = new Date();
                let age = now.getFullYear() - dobDate.getFullYear();
                const monthDiff = now.getMonth() - dobDate.getMonth();
                if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dobDate.getDate())) {
                    age--;
                }
                if (age < 18) {
                    youthTag = "Y";
                }
            }
            const gearAndYouth = [student.HasEquipment ? "G" : null, youthTag ? youthTag : null].filter(Boolean).join(" ");
            const expandedStudent: StudentListType = { 
                ...student, 
                Mobile: formattedMobile, 
                Package: signup.node.fs_registrations.fs_purchases.Package ? "Pkg": "", 
                attended: Boolean(histRecord), 
                histId: histRecord?.node.id,
                GearAndYouth: gearAndYouth
            };
            rstudents.push(expandedStudent);
        }
    });

    const handleCheckboxChange = (student) => async (e) => {
        if (e.target.checked) {
            await addAttendanceMutation({ variables: { ah: { class: event.class, classname: event.fs_classes.name, datetaken: event.date, student: student.id, event: event.id } } });
        }
        else {
            await deleteAttendanceMutation({ variables: { filter: { id: { eq: student.histId } } } });
        }
        setRefetchAttendance(prev => !prev);
    };

    return (
        <BoxRoot>
            <Card sx={{
                ...(isSmallScreen
                    ? { padding: "4px 0px 8px" }
                    : { padding: "4px 16px 8px" })
            }}>
                <BoxRoot ownerState={{
                    variant: "contained",
                    color: "white",
                    opacity: 1,
                    bgColor: "secondary",
                    coloredShadow: "secondary"
                }}
                    sx={{ display: 'flex', width: '100%' }}
                    borderRadius={2}
                    pb={1} px={2} mt={-1} >
                                        <TypographyRoot variant="h3" ownerState={{ color: "white" }}>
                                                Event Roster
                                        </TypographyRoot>
                                        <BoxRoot sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
                                            <ButtonRoot
                                                variant="contained"
                                                onClick={() => {
                                                    const attendedStudentIds = new Set<number>(attendance.map(a => a.node.student));

                                                    const recipients: Array<{ email: string; firstName: string }> = [];
                                                    signups.forEach(su => {
                                                        const student = su.node.fs_registrations.fs_purchases.fs_students;
                                                        if (attendedStudentIds.has(student.id) && student.Email) {
                                                            recipients.push({ email: student.Email, firstName: student.FirstName ?? '' });
                                                        }
                                                    });

                                                    if (!recipients.length) {
                                                        // no attendees with email
                                                        alert('No attended students with email found to send follow-up to.');
                                                        return;
                                                    }

                                                    if (!event.fs_classes?.followupmsg) {
                                                        alert('No follow-up message configured for this class.');
                                                        return;
                                                    }

                                                    // Parse the follow-up message for Subject (first line), optional From (second line), and Body (rest)
                                                    const lines = (event.fs_classes?.followupmsg || '').split('\n');
                                                    const subject = lines[0] || `Follow-up: ${event.fs_classes?.name ?? ''}`;
                                                    // If second line looks like an email, treat it as from and skip it
                                                    //const secondIsEmail = lines[1] && /\S+@\S+\.\S+/.test(lines[1]);
                                                    //const bodyLines = secondIsEmail ? lines.slice(2) : lines.slice(1);
                                                    const bodyLines = lines.slice(1);
                                                    const body = bodyLines.join('\n');

                                                    const base = { subject: subject, body };
                                                    setFollowupPayload({ base, recipients });
                                                    setFollowupDialogOpen(true);
                                                }}
                                            >
                                                Send Follow-up
                                            </ButtonRoot>
                                        </BoxRoot>
                </BoxRoot>
                <BoxRoot px={1}>
                    <TypographyRoot variant="h5" ownerState={{ color: "secondary" }}>
                        All information is confidential and for official business only
                    </TypographyRoot>
                </BoxRoot>


                <BoxRoot component="table" sx={{ borderSpacing: '6px', textAlign: "left", width: "100%" }}>
                    <BoxRoot component="tr">
                        <BoxRoot component="td">
                            <TypographyRoot variant="h5" ownerState={{ color: "dark" }}>Event Status:</TypographyRoot>
                        </BoxRoot>
                        <BoxRoot component="td">
                            <TypographyRoot variant="h5" ownerState={{ color: "secondary" }}>{getEventStatus(event.status)}</TypographyRoot>
                        </BoxRoot>
                    </BoxRoot>
                    <BoxRoot component="tr">
                        <BoxRoot component="td">
                            <TypographyRoot variant="h5" ownerState={{ color: "dark" }}>Capacity:</TypographyRoot>
                        </BoxRoot>
                        <BoxRoot component="td">
                            <TypographyRoot variant="h5" ownerState={{ color: "secondary" }}>{event.capacity}</TypographyRoot>
                        </BoxRoot>
                    </BoxRoot>
                    <BoxRoot component="tr">
                        <BoxRoot component="td">
                            <TypographyRoot variant="h5" ownerState={{ color: "dark" }}>Registered:</TypographyRoot>
                        </BoxRoot>
                        <BoxRoot component="td">
                            <TypographyRoot variant="h5" ownerState={{ color: "secondary" }} >{registered}</TypographyRoot>
                        </BoxRoot>
                    </BoxRoot>

                    <BoxRoot component="tr">
                        <BoxRoot component="td">
                            <TypographyRoot variant="h6">Instructors:</TypographyRoot>
                        </BoxRoot>
                        <BoxRoot component="td">
                            <List>
                                {instructors?.map((instructorEdge) => {
                                    const instructor = instructorEdge.node;
                                    if (instructor.on_deck) return null;
                                    return (
                                        <ListItem key={instructor.id}>
                                            <TypographyRoot variant="h5" ownerState={{ color: "secondary" }}>
                                                <ListItemText disableTypography={true}
                                                    primary={`${instructor.fs_admins?.first_name} ${instructor.fs_admins?.last_name}`}
                                                />
                                            </TypographyRoot>
                                        </ListItem>
                                    );
                                })
                                }
                            </List>
                            {(!instructors?.filter(i => !i.node.on_deck).length)
                                && <TypographyRoot variant="h5" ownerState={{ color: "secondary" }}>N/A</TypographyRoot>}
                        </BoxRoot>
                    </BoxRoot>
                    <BoxRoot component="tr">
                        <BoxRoot component="td">
                            <TypographyRoot variant="h6">Instructors on Deck:</TypographyRoot>
                        </BoxRoot>
                        <BoxRoot component="td">
                            <List>
                                {instructors?.map((instructorEdge) => {
                                    const instructor = instructorEdge.node;
                                    if (!instructor.on_deck) return null;
                                    return (
                                        <ListItem key={instructor.id}>
                                            <TypographyRoot variant="h5" ownerState={{ color: "secondary" }}>
                                                <ListItemText disableTypography={true}
                                                    primary={`${instructor.fs_admins?.first_name} ${instructor.fs_admins?.last_name}`}
                                                /></TypographyRoot>
                                        </ListItem>
                                    );
                                })}
                            </List>
                            {(!instructors?.filter(i => i.node.on_deck).length) &&
                                <TypographyRoot variant="h5" ownerState={{ color: "secondary" }}>N/A</TypographyRoot>}
                        </BoxRoot>
                    </BoxRoot>
                    <BoxRoot component="tr">
                        <BoxRoot component="td">
                            <TypographyRoot variant="h6">Medical Conditions:</TypographyRoot>
                        </BoxRoot>
                        <BoxRoot component="td">
                            <List>
                                {signups
                                    .map((signupEdge) => {
                                        const student = signupEdge.node.fs_registrations.fs_purchases.fs_students;
                                        if (!student.MedCondFlag) return null;
                                        return (
                                            <ListItem>
                                                <TypographyRoot variant="h5" ownerState={{ color: "secondary" }} >
                                                    <ButtonRoot variant="contained" onClick={handlePopoverOpen(student.id)} sx={{ marginBottom: '6px' }}>
                                                        {student.FirstName} {student.LastName}
                                                    </ButtonRoot>
                                                    <Popover
                                                        id={"medid-" + student.id}
                                                        open={Boolean(anchorElMap[student.id])}
                                                        anchorEl={anchorElMap[student.id]}
                                                        onClose={handlePopoverClose(student.id)}
                                                        anchorOrigin={{
                                                            vertical: 'center',
                                                            horizontal: 'center',
                                                        }}
                                                        sx={{ padding: 0 }}
                                                    >
                                                        <BoxRoot ownerState={{ bgColor: "dark", borderRadius: "lg" }}>
                                                            <TypographyRoot variant="h5"
                                                                ownerState={{ color: "white" }}
                                                                px={2} py={2}>
                                                                {student.MedCondDescr}
                                                            </TypographyRoot>
                                                        </BoxRoot>
                                                    </Popover>
                                                </TypographyRoot>
                                            </ListItem>)
                                    })}
                            </List>
                            {(!signups.filter(s => s.node.fs_registrations.fs_purchases.fs_students.MedCondFlag).length) &&
                                <TypographyRoot variant="h5" ownerState={{ color: "secondary" }}>N/A</TypographyRoot>}
                        </BoxRoot>
                    </BoxRoot>
                </BoxRoot>
            </Card>

            <BoxRoot component="table" sx={{ borderSpacing: '6px', textAlign: "left", width: "100%", }}>
                <BoxRoot component="tr">
                    <BoxRoot component="td">
                        <TypographyRoot variant="h6">Students:</TypographyRoot>
                    </BoxRoot>
                    <BoxRoot component="td"></BoxRoot>
                </BoxRoot>
                <BoxRoot component="tr">
                    <BoxRoot component="td" {...{ colSpan: 2 }}>
                        <TypographyRoot variant="h5" ownerState={{ color: "secondary" }}>
                            <BoxRoot component="table">
                                <BoxRoot component="tbody">
                                {rstudents.map((student) => (
                                    <Fragment key={student.id}>
                                        {/* First Row */}
                                        <BoxRoot component="tr">
                                            {/* Checkbox (centered between rows) */}
                                            <td rowSpan={2} style={{ verticalAlign: 'middle', textAlign: 'center', borderBottom: 'solid 1px gray'}}>
                                                <Checkbox checked={student.attended} onChange={handleCheckboxChange(student)} />
                                            </td>
                                            {/* Name (First) */}
                                            <BoxRoot component="td" px={1} borderBottom="none">
                                                {student.FirstName}
                                            </BoxRoot>
                                            {/* Height */}
                                            <BoxRoot component="td" px={1} textAlign="left" borderBottom="none">
                                                {student.Height} 
                                            </BoxRoot>
                                            {/* Weight */}
                                            <BoxRoot component="td" px={1} textAlign="left" borderBottom="none">
                                                {student.Weight}
                                            </BoxRoot>
                                            {/* Package */}
                                            <BoxRoot component="td" px={1} textAlign="left" borderBottom="none">
                                                {student.Package}
                                            </BoxRoot>
                                            <BoxRoot component="td" px={1} textAlign="left" borderBottom="none">
                                                {
                                                    student.GearAndYouth
                                                }

                                            </BoxRoot>
                                        </BoxRoot>
                                        {/* Second Row */}
                                        <BoxRoot component="tr">
                                            {/* Name (Last) */}
                                            <BoxRoot component="td" px={1} style={{ borderBottom: 'solid 1px gray' }}>
                                                {student.LastName}
                                            </BoxRoot>
                                            {/* Mobile */}
                                            <td colSpan={2} style={{ borderBottom: 'solid 1px gray', textAlign: 'left' }}>
                                                {student.Mobile ? (
                                                    <a
                                                        href={`tel:${student.Mobile}`}
                                                        style={{ color: theme.palette.primary.main, textDecoration: 'underline' }}
                                                    >
                                                        {student.Mobile}
                                                    </a>
                                                ) : null}
                                            </td>
                                            {/* Registration State */}
                                            <BoxRoot component="td" px={1} textAlign="left" style={{ borderBottom: 'solid 1px gray' }}>
                                                {student.RegistrationState >= 20 ? "W" : ""}
                                            </BoxRoot>
                                            {/* Med condition */}
                                            <BoxRoot component="td" px={1} style={{ borderBottom: 'solid 1px gray' }} > 
                                                {student.MedCondFlag 
                                                && (
                                                <TypographyRoot ownerState={{ color: "error" }}>
                                                    Med!
                                                </TypographyRoot>
                                                )} 
                                            </BoxRoot>
                                        </BoxRoot>

                                    </Fragment>
                                ))}</BoxRoot></BoxRoot>
                        </TypographyRoot>
                    </BoxRoot>
                </BoxRoot>
            </BoxRoot>
            <Dialog open={followupDialogOpen} onClose={() => setFollowupDialogOpen(false)} fullScreen={isSmallScreen}>
                <DialogTitle>Confirm Send Follow-up</DialogTitle>
                <DialogContent>
                    <TypographyRoot variant="body1">You are about to send a follow-up message to {followupPayload?.recipients.length ?? 0} recipients. Proceed?</TypographyRoot>
                    {sendingFollowup && (
                        <BoxRoot sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                            <CircularProgress size={20} />
                            <TypographyRoot variant="body2">Sending {sendProgress.current} / {sendProgress.total}</TypographyRoot>
                        </BoxRoot>
                    )}
                </DialogContent>
                <DialogActions>
                    <ButtonRoot onClick={() => { if (!sendingFollowup) { setFollowupDialogOpen(false); setFollowupPayload(null); } }}>Cancel</ButtonRoot>
                    <ButtonRoot
                        variant="contained"
                        onClick={async () => {
                            if (!followupPayload) return;
                            const recipients = followupPayload.recipients;
                            setSendingFollowup(true);
                            setSendProgress({ current: 0, total: recipients.length });
                            try {
                                for (let i = 0; i < recipients.length; i++) {
                                    const r = recipients[i];
                                    const personalizedBody = followupPayload.base.body.replace(/\{first\}/g, r.firstName || '');

                                    const payload = { to: r.email, subject: followupPayload.base.subject, body: personalizedBody };
                                    const resp = await fetch(`${process.env.REACT_APP_NETLIFY_URL || ''}/.netlify/functions/send-email`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify(payload),
                                    });

                                    const result = await resp.json().catch(() => null);
                                    if (!resp.ok || result?.success === false) {
                                        console.error('send to', r.email, 'failed', result);
                                        // continue on failure but log
                                    }

                                    setSendProgress(prev => ({ current: prev.current + 1, total: prev.total }));
                                    // small throttle to avoid rate limits
                                    await new Promise(res => setTimeout(res, 200));
                                }

                                alert(`Follow-up sent to ${recipients.length} recipients.`);
                            } catch (err: any) {
                                console.error(err);
                                alert('Error sending follow-up: ' + (err?.message ?? String(err)));
                            } finally {
                                setSendingFollowup(false);
                                setFollowupDialogOpen(false);
                                setFollowupPayload(null);
                                setSendProgress({ current: 0, total: 0 });
                            }
                        }}
                    >
                        Send
                    </ButtonRoot>
                </DialogActions>
            </Dialog>
        </BoxRoot>
    )
}

export default EventRoster;

function countSignupsWithStatusZero(data: types.Fs_SignupEdge[]) {
    let count = 0;

    data.forEach(signupEdge => {
        const signup = signupEdge.node;

        // Check if the status of the signup is 0
        if (signup.status === 0) {
            count++;
        }
    });

    return count;
}

// TODO: Move to utils
const getEventStatus = (s) => { // 0-Scheduled, 1-completed, 2-canceled
    return (s == 0) && "Scheduled"
        || (s == 1) && "Completed"
        || (s == 2) && "Canceled";
}; 
